import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Baby, Shield, CreditCard, Users, BookOpen, AlertTriangle, XCircle } from 'lucide-react';

const ChildrensPrivacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white shadow-xl" style={{ borderRadius: '24px' }}>
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-pink-100 rounded-2xl">
              <Baby className="w-10 h-10 text-pink-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Children's Use & Parental Consent</h1>
              <p className="text-gray-500">COPPA Compliance</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-8">Last Updated: 1/1/26</p>

          <div className="prose prose-lg max-w-none text-gray-700">
            <div className="bg-pink-50 border-l-4 border-pink-500 p-4 mb-8 rounded-r-lg">
              <p className="font-semibold text-pink-800">
                Little Genius Hub is designed for children under the age of 13 and is compliant with the Children's Online Privacy Protection Act (COPPA).
              </p>
            </div>

            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Children may only use the Service with verifiable parental or legal guardian consent.</li>
              <li>Parents or guardians are responsible for supervising their child's use of the Service.</li>
              <li>We do not knowingly collect personal information from children without parental consent.</li>
              <li>Parents may review, delete, or request changes to their child's information at any time by contacting us.</li>
            </ul>
            <p className="mb-8">
              Contact: <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a>
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-pink-500" />
              2. Account Registration
            </h2>
            <p className="mb-4">To use certain features, a parent or guardian must create an account.</p>
            <p className="mb-2">You agree to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Provide accurate and current information</li>
              <li>Maintain the security of your login credentials</li>
              <li>Accept responsibility for all activity under your account</li>
            </ul>
            <p className="mb-6">Accounts created with false information may be suspended or terminated.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-pink-500" />
              3. Subscriptions & Payments
            </h2>
            <p className="mb-4">Some features require a monthly or recurring subscription.</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Payments are billed automatically unless canceled.</li>
              <li>Subscriptions can be canceled anytime through account settings.</li>
              <li>Fees are non-refundable, except where required by law.</li>
              <li>Access continues until the end of the billing period.</li>
            </ul>
            <p className="mb-4">We reserve the right to update pricing with advance notice.</p>

            {/* Store Billing Acknowledgment */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-blue-800 mb-3">Store Billing Acknowledgment</p>
              <p className="text-blue-800 mb-2">By subscribing through our mobile application, parents acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-1 text-blue-800">
                <li>Subscription charges are processed by <strong>Apple</strong> (iOS) or <strong>Google</strong> (Android), not Little Genius Hub</li>
                <li>Subscription management, including cancellation and renewal, is handled through your Apple ID or Google account settings</li>
                <li>Billing inquiries should be directed to Apple or Google through their respective support channels</li>
                <li>Refund requests must follow Apple App Store or Google Play Store policies</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-pink-500" />
              4. Acceptable Use Policy
            </h2>
            <p className="mb-4">Users may not:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Upload inappropriate, abusive, or harmful content</li>
              <li>Attempt to exploit, hack, or reverse-engineer the app</li>
              <li>Use the Service for unlawful purposes</li>
              <li>Harass, threaten, or harm others</li>
              <li>Misuse educational tools or progress systems</li>
            </ul>
            <p className="mb-6">We reserve the right to remove content or restrict accounts that violate these rules.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">5. User Content</h2>
            <p className="mb-4">Users or parents may submit limited content (such as names, avatars, or learning progress).</p>
            <p className="mb-2">By submitting content, you confirm:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>You have the right to share it</li>
              <li>It does not violate laws or third-party rights</li>
              <li>You grant us permission to use it solely to operate and improve the Service</li>
            </ul>
            <p className="mb-6 font-bold text-green-700">We do not sell children's personal data.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-pink-500" />
              6. Intellectual Property
            </h2>
            <p className="mb-4">All content, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Software</li>
              <li>Graphics</li>
              <li>Characters</li>
              <li>Animations</li>
              <li>Logos</li>
              <li>Educational materials</li>
            </ul>
            <p className="mb-6">are the exclusive property of Little Genius Hub or its licensors. You may not copy, distribute, reproduce, or modify any part of the Service without written permission.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">7. Account Suspension or Termination</h2>
            <p className="mb-4">We may suspend or terminate accounts if:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>These Terms are violated</li>
              <li>There is misuse of the platform</li>
              <li>There is risk to users, systems, or compliance</li>
            </ul>
            <p className="mb-6">Parents may request account deletion at any time.</p>

            {/* Withdrawal of Consent Statement */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <XCircle className="w-6 h-6 text-pink-500" />
              8. Withdrawal of Consent
            </h2>
            <div className="bg-pink-50 border-l-4 border-pink-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-pink-800 mb-3">Parental Rights to Withdraw Consent</p>
              <p className="text-pink-800 mb-2">Parents or guardians have the explicit right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-pink-800">
                <li><strong>Withdraw consent</strong> at any time for the collection and use of their child's information</li>
                <li><strong>Request deletion</strong> of all data associated with their child's account</li>
                <li><strong>Cancel subscription</strong> through Apple ID settings (iOS) or Google Play account settings (Android)</li>
                <li><strong>Request account termination</strong> by contacting us directly</li>
              </ul>
              <p className="text-pink-800 mt-3">To exercise these rights, contact: <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a></p>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">9. Educational Disclaimer</h2>
            <p className="mb-6">
              Little Genius Hub is designed to support learning but does not guarantee specific educational outcomes. Content is provided for educational and entertainment purposes only.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-pink-500" />
              10. Disclaimer of Warranties
            </h2>
            <p className="mb-4">The Service is provided "as is" and "as available."</p>
            <p className="mb-2">We do not guarantee:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Error-free performance</li>
              <li>Continuous availability</li>
              <li>Compatibility with all devices</li>
            </ul>
            <p className="mb-6">Use of the Service is at your own risk.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">11. Limitation of Liability</h2>
            <p className="mb-4">To the fullest extent permitted by law, Little Genius Hub and its affiliates shall not be liable for:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Indirect or incidental damages</li>
              <li>Loss of data or progress</li>
              <li>Technical issues or service interruptions</li>
            </ul>
            <p className="mb-6">Our total liability shall not exceed the amount paid in the last 12 months.</p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">12. Privacy Policy</h2>
            <p className="mb-6">
              Your use of the Service is also governed by our <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>, which explains how we collect, use, and protect personal data—especially for children.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">13. Changes to These Terms</h2>
            <p className="mb-6">
              We may update these Terms from time to time. Continued use of the Service after changes means acceptance of the updated Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">14. Governing Law</h2>
            <p className="mb-6">
              These Terms are governed by the laws of the United States, without regard to conflict of law principles.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">15. Contact Information</h2>
            <p className="mb-4">For questions, concerns, or parental requests, contact:</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p><strong>Email:</strong> <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a></p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChildrensPrivacy;
